/**
 * AttemptWorkspace.jsx — Phase 2B Student Attempt Experience
 *
 * Critical Phase 2B invariants implemented:
 *   • Renders ONLY from frozen delivery manifest (item_version_id + content snapshot)
 *   • NEVER shows: correct_response, scoring keys, rubric internals, difficulty,
 *     AI provenance, reviewer notes
 *   • Autosave with deterministic retry (Saving… → Saved / Save failed)
 *   • Submission requires explicit confirmation
 *   • Immutable submission receipt (no scores — Phase 2C is blocked)
 *   • Countdown timer respects remaining time from server
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, Clock, AlertTriangle, ChevronLeft, ChevronRight, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` }; }
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" }, ...opts,
  });
  if (!r.ok) {
    let msg;
    try {
      const body = await r.json();
      msg =
        body?.detail?.message ||
        body?.detail?.conflict ||
        (typeof body?.detail === "string" ? body.detail : null) ||
        body?.message ||
        r.statusText;
    } catch { msg = r.statusText; }
    throw Object.assign(new Error(msg || r.statusText), { status: r.status });
  }
  return r.json();
}

// Autosave debounce delay (ms)
const AUTOSAVE_DEBOUNCE = 2000;
// Retry intervals (ms) — exponential back-off
const RETRY_DELAYS = [2000, 5000, 15000];

export default function AttemptWorkspace() {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  // Core state
  const [attempt, setAttempt]       = useState(null);
  const [manifest, setManifest]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [phase, setPhase]           = useState("pre_start"); // pre_start | workspace | submitted

  // Workspace state
  const [items, setItems]           = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [responses, setResponses]   = useState({}); // {item_version_id: responseData}
  const [savedItems, setSavedItems] = useState(new Set()); // item_version_ids confirmed saved
  const [savingItems, setSavingItems] = useState(new Set()); // currently saving
  const [failedItems, setFailedItems] = useState(new Set()); // save failed
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [loadError, setLoadError]   = useState(null); // 403/404 error message
  const [sessionDuration, setSessionDuration] = useState(null); // for pre_start phase

  // Timer state
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [timedOut, setTimedOut]       = useState(false);
  const timerRef = useRef(null);
  const autoSaveTimers = useRef({}); // {item_version_id: timeoutId}

  // ── Internal workspace opener (shared between loadAttemptData and handleStartAttempt)
  const openWorkspaceRef = useRef(null);
  openWorkspaceRef.current = async (att) => {
    try {
      // Get manifest (frozen content snapshot — sealed at open_attempt time)
      const man = await apiFetch(`/delivery/attempts/${att.id}/manifest`);
      setManifest(man);
      const mdata = typeof man.manifest_data === "string"
        ? JSON.parse(man.manifest_data)
        : man.manifest_data;

      // Build item list from manifest (student-safe content only — no correct answers)
      // manifest_data.item_versions is the top-level list of frozen item content
      const itemVersions = mdata.item_versions || [];
      const itemList = itemVersions.map((iv, idx) => ({
        idx,
        item_id: iv.item_id,
        item_version_id: iv.item_version_id,
        version_no: iv.version_no,
        item_type: iv.item_type,
        component_id: iv.component_id,
        // STUDENT-VISIBLE ONLY (frozen at manifest seal time — never from live Item Bank)
        stem: iv.stem || "No question text available.",
        options: iv.options || null,
        source_material_ref: iv.source_material_ref || null,
        qti_interaction_type: iv.qti_interaction_type || null,
        // Explicit exclusion: correct_response, difficulty, ai_generated, reviewer notes
        // are NOT mapped — they are never surfaced to the student UI.
      }));
      setItems(itemList);

      // Load existing responses (for resume after refresh/reconnect)
      const existingResps = await apiFetch(`/delivery/attempts/${att.id}/responses`);
      const respMap = existingResps.reduce(
        (acc, r) => ({ ...acc, [r.item_version_id]: r.response_data }),
        {}
      );
      setResponses(respMap);
      // Mark existing saved items
      setSavedItems(new Set(existingResps.map(r => r.item_version_id)));

      // Timer — computed from server-authoritative time_limit_ends_at
      if (att.time_limit_ends_at) {
        const endsAt = new Date(att.time_limit_ends_at.replace("Z", "+00:00")).getTime();
        const now = Date.now();
        const secs = Math.max(0, Math.round((endsAt - now) / 1000));
        setSecondsLeft(secs);
      }
      setPhase("workspace");
    } catch (e) {
      toast.error("Failed to open workspace: " + e.message);
    }
  };

  // ── Load attempt + manifest ─────────────────────────────────────────────
  const loadAttemptData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const att = await apiFetch(`/delivery/attempts/${attemptId}`);
      setAttempt(att);
      // Load session duration for pre-start instructions (attempt is NOT_STARTED, manifest not yet sealed)
      if (att.status === "NOT_STARTED" && att.session_id) {
        apiFetch(`/delivery/sessions/${att.session_id}`)
          .then(s => setSessionDuration(s.duration_minutes))
          .catch(() => {});
      }
      if (att.status === "SUBMITTED" || att.status === "VOIDED" || att.status === "ABANDONED") {
        setPhase("submitted");
        // Load existing responses for submitted receipt (read-only)
        const resps = await apiFetch(`/delivery/attempts/${attemptId}/responses`);
        setResponses(
          resps.reduce((acc, r) => ({ ...acc, [r.item_version_id]: r.response_data }), {})
        );
      } else if (att.status === "IN_PROGRESS") {
        await openWorkspaceRef.current(att);
      }
      // NOT_STARTED → show pre-start instructions page
    } catch (e) {
      if (e.status === 403) {
        setLoadError("You do not have permission to access this attempt.");
      } else if (e.status === 404) {
        setLoadError("Attempt not found.");
      } else {
        setLoadError("Load failed: " + (e.message || "Unexpected error. Please try again."));
      }
      toast.error(e.message || "Failed to load attempt");
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => { loadAttemptData(); }, [loadAttemptData]);

  // ── Countdown timer ─────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "workspace" || secondsLeft === null) return;
    timerRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimedOut(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, secondsLeft]);

  // ── Open attempt (NOT_STARTED → IN_PROGRESS) ────────────────────────────
  const handleStartAttempt = async () => {
    try {
      const opened = await apiFetch(`/delivery/attempts/${attemptId}/open`, { method: "POST" });
      setAttempt(opened);
      await openWorkspaceRef.current(opened);
    } catch (e) {
      if (e.status === 409) {
        toast.error(`Cannot start: ${e.message}`);
      } else {
        toast.error("Could not start: " + e.message);
      }
    }
  };

  // ── Autosave logic ──────────────────────────────────────────────────────
  const persistResponse = useCallback(async (item, responseData, retryCount = 0) => {
    const ivId = item.item_version_id;
    try {
      setSavingItems(prev => new Set([...prev, ivId]));
      setFailedItems(prev => { const s = new Set(prev); s.delete(ivId); return s; });

      await apiFetch(`/delivery/attempts/${attemptId}/responses`, {
        method: "POST",
        body: JSON.stringify({
          item_id:          item.item_id,
          item_version_id:  ivId,
          interaction_type: item.item_type || "MCQ",
          response_data:    responseData,
          sequence:         item.idx + 1,
        }),
      });

      setSavedItems(prev => new Set([...prev, ivId]));
      setSavingItems(prev => { const s = new Set(prev); s.delete(ivId); return s; });
      setFailedItems(prev => { const s = new Set(prev); s.delete(ivId); return s; });
    } catch (e) {
      setSavingItems(prev => { const s = new Set(prev); s.delete(ivId); return s; });
      if (retryCount < RETRY_DELAYS.length) {
        const delay = RETRY_DELAYS[retryCount];
        setTimeout(() => persistResponse(item, responseData, retryCount + 1), delay);
      } else {
        setFailedItems(prev => new Set([...prev, ivId]));
        toast.error(`Save failed for item ${item.idx + 1}. Retrying…`);
      }
    }
  }, [attemptId]);

  const handleResponseChange = useCallback((item, responseData) => {
    const ivId = item.item_version_id;
    // Update local state immediately
    setResponses(prev => ({ ...prev, [ivId]: responseData }));
    setSavedItems(prev => { const s = new Set(prev); s.delete(ivId); return s; });

    // Debounce autosave
    if (autoSaveTimers.current[ivId]) clearTimeout(autoSaveTimers.current[ivId]);
    autoSaveTimers.current[ivId] = setTimeout(
      () => persistResponse(item, responseData),
      AUTOSAVE_DEBOUNCE
    );
  }, [persistResponse]);

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Flush all pending autosaves
      Object.values(autoSaveTimers.current).forEach(t => clearTimeout(t));
      autoSaveTimers.current = {};

      const result = await apiFetch(`/delivery/attempts/${attemptId}/submit`, { method: "POST" });
      setAttempt(result);
      clearInterval(timerRef.current);
      setPhase("submitted");
      setShowConfirmSubmit(false);
      toast.success("Exam submitted successfully");
    } catch (e) {
      toast.error("Submit failed: " + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Timeout auto-submit ─────────────────────────────────────────────────
  useEffect(() => {
    if (!timedOut || phase !== "workspace") return;
    if (attempt?.timeout_action === "AUTO_SUBMIT") {
      toast.info("Time expired — auto-submitting your responses…");
      handleSubmit();
    } else if (attempt?.timeout_action === "LOCK_NO_SUBMIT") {
      toast.warning("Time expired. Your attempt has been locked.");
    }
  }, [timedOut]);

  // ── Timer display ───────────────────────────────────────────────────────
  const formatTime = (secs) => {
    if (secs === null) return "--:--";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const unansweredCount = items.filter(it => !responses[it.item_version_id]).length;

  // ══════════════════════════════════════════════════════════════════════
  //  PHASE: loading
  // ══════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center"
           data-testid="attempt-loading">
        <p className="text-slate-400 text-sm">Loading exam…</p>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  //  ERROR: 403 / 404 — ownership violation or not found
  // ══════════════════════════════════════════════════════════════════════
  if (loadError) {
    const is403 = loadError.includes("permission") || loadError.includes("denied");
    const is404 = loadError.includes("not found") || loadError.includes("Not found");
    const errorTitle = is403 ? "Access Denied" : is404 ? "Not Found" : "Error";
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4"
           data-testid="attempt-error">
        <div className="max-w-md w-full rounded-2xl border border-red-800/40 bg-slate-900/80 px-8 py-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-slate-100 mb-2">{errorTitle}</h1>
          <p className="text-sm text-slate-400 mb-6">
            {loadError || "An unexpected error occurred. Please try again."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/my-exams")}
            data-testid="back-to-my-exams-error"
            className="px-6 py-2 rounded-xl text-sm font-medium bg-slate-700 hover:bg-slate-600
                       text-white transition-colors"
          >
            Back to My Exams
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  //  PHASE: pre_start — instructions + start button
  // ══════════════════════════════════════════════════════════════════════
  if (phase === "pre_start") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4"
           data-testid="attempt-prestart">
        <div className="max-w-xl w-full">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-slate-800">
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
                Exam Instructions
              </span>
              <h1 className="text-xl font-bold text-slate-100 mt-2">
                {attempt?.offering_title || "Assessment"}
              </h1>
            </div>
            {/* Instructions */}
            <div className="px-8 py-6 space-y-4">
              <InfoRow icon={Clock} label="Duration"
                value={
                  manifest?.duration_minutes
                    ? `${manifest.duration_minutes} min`
                    : sessionDuration
                    ? `${sessionDuration} min`
                    : "Check with your invigilator"
                }
              />
              <InfoRow icon={ClipboardList} label="Questions"
                value={items.length > 0 ? `${items.length} item${items.length !== 1 ? "s" : ""}` : "Loading…"} />
              <InfoRow icon={RotateCcw} label="Attempts Allowed"
                value={`Attempt #${(attempt?.attempt_number || 1)}`} />

              <div className="rounded-xl bg-amber-950/20 border border-amber-800/40 p-4 mt-4">
                <p className="text-xs font-semibold text-amber-300 mb-2">Before you begin</p>
                <ul className="text-xs text-amber-200/80 space-y-1 list-disc list-inside">
                  <li>Your responses are autosaved as you work.</li>
                  <li>Do not refresh or navigate away during the exam.</li>
                  <li>You can review and change answers before submitting.</li>
                  <li>Once submitted, your answers cannot be changed.</li>
                  <li>Scores will be released after marking — not immediately.</li>
                </ul>
              </div>
            </div>
            {/* Start */}
            <div className="px-8 py-6 border-t border-slate-800 flex gap-3">
              <button
                type="button"
                onClick={handleStartAttempt}
                data-testid="start-attempt-btn"
                className="flex-1 py-3 rounded-xl text-sm font-semibold bg-emerald-700
                           hover:bg-emerald-600 text-white transition-colors"
              >
                Start Attempt
              </button>
              <button
                type="button"
                onClick={() => navigate("/my-exams")}
                data-testid="cancel-attempt-btn"
                className="px-4 py-3 rounded-xl text-sm text-slate-400 hover:text-slate-200
                           border border-slate-700 hover:border-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  //  PHASE: submitted — immutable receipt
  // ══════════════════════════════════════════════════════════════════════
  if (phase === "submitted") {
    const isVoided    = attempt?.status === "VOIDED";
    const isAbandoned = attempt?.status === "ABANDONED";
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4"
           data-testid="attempt-receipt">
        <div className="max-w-lg w-full rounded-2xl border bg-slate-900/80 overflow-hidden
                        border-slate-800">
          <div className="px-8 py-8 text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              isVoided || isAbandoned
                ? "bg-red-900/40" : "bg-emerald-900/40"
            }`}>
              {isVoided || isAbandoned
                ? <AlertTriangle className="w-8 h-8 text-red-400" />
                : <CheckCircle className="w-8 h-8 text-emerald-400" />
              }
            </div>
            <h1 className="text-2xl font-bold text-slate-100 mb-2">
              {isVoided ? "Attempt Voided" : isAbandoned ? "Attempt Abandoned" : "Submission Received"}
            </h1>
            <p className="text-sm text-slate-400 mb-6">
              {isVoided || isAbandoned
                ? "This attempt has been closed. Please contact your exam office."
                : "Your responses have been recorded. Scores will be released after marking."}
            </p>

            {/* Receipt details */}
            <div className="rounded-xl bg-slate-800/40 border border-slate-700/60 p-5 text-left
                            space-y-3 mb-6" data-testid="submission-receipt-details">
              <ReceiptRow label="Attempt ID" value={attempt?.id?.slice(0, 18) + "…"} />
              <ReceiptRow label="Status"     value={attempt?.status} />
              <ReceiptRow label="Submitted"  value={attempt?.submitted_at?.slice(0, 19)?.replace("T", " ") || "—"} />
              {attempt?.force_submitted && (
                <ReceiptRow label="Note" value="Time-limit submission" warning />
              )}
              {/* Phase 2B boundary: no scores shown */}
              <p className="text-xs text-slate-600 pt-2 border-t border-slate-700/60">
                Scores and grades are not available until the marking period closes (Phase 2C).
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/my-exams")}
              data-testid="receipt-back-btn"
              className="w-full py-3 rounded-xl text-sm font-semibold border border-slate-700
                         text-slate-300 hover:text-slate-100 hover:border-slate-600 transition-colors"
            >
              Back to My Exams
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  //  PHASE: workspace — timed exam
  // ══════════════════════════════════════════════════════════════════════
  const currentItem = items[currentIdx];
  const totalItems  = items.length;

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="attempt-workspace">
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <header className="border-b border-slate-800 bg-slate-900/80 px-6 py-3 flex items-center gap-4
                         sticky top-0 z-10" data-testid="workspace-header">
        <div className="flex-1 min-w-0">
          <span className="text-xs text-slate-500 font-medium truncate">
            {attempt?.offering_title || "Assessment"}
          </span>
        </div>
        {/* Autosave indicator */}
        <AutosaveIndicator
          saving={savingItems.size > 0}
          failed={failedItems.size > 0}
        />
        {/* Timer */}
        {secondsLeft !== null && (
          <div
            className={`flex items-center gap-1.5 text-sm font-mono font-semibold px-3 py-1
                         rounded-lg border ${
              secondsLeft < 300
                ? "text-red-300 border-red-700/60 bg-red-950/30 animate-pulse"
                : secondsLeft < 600
                  ? "text-amber-300 border-amber-700/60 bg-amber-950/30"
                  : "text-slate-300 border-slate-700 bg-slate-900/60"
            }`}
            data-testid="exam-timer"
          >
            <Clock className="w-3.5 h-3.5" />
            {formatTime(secondsLeft)}
          </div>
        )}
        {/* Submit */}
        {!timedOut && (
          <button
            type="button"
            onClick={() => setShowConfirmSubmit(true)}
            data-testid="submit-exam-btn"
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold
                       text-white bg-emerald-700 hover:bg-emerald-600 transition-colors"
          >
            <Send className="w-3.5 h-3.5" /> Submit Exam
          </button>
        )}
      </header>

      <div className="flex flex-1 min-h-0">
        {/* ── Item navigator (left sidebar) ──────────────────────── */}
        <aside className="w-48 flex-shrink-0 border-r border-slate-800 bg-slate-900/40 p-4
                           overflow-y-auto hidden md:block" data-testid="item-navigator">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            Questions
          </p>
          <div className="grid grid-cols-4 gap-1.5">
            {items.map((item, idx) => {
              const answered = !!responses[item.item_version_id];
              const isCurrent = idx === currentIdx;
              const isSaved = savedItems.has(item.item_version_id);
              return (
                <button
                  key={`${item.component_id}-${item.item_version_id}-${idx}`}
                  type="button"
                  onClick={() => setCurrentIdx(idx)}
                  data-testid={`nav-item-${idx + 1}`}
                  title={answered ? "Answered" : "Not answered"}
                  className={`w-9 h-9 rounded-lg text-xs font-semibold border transition-all ${
                    isCurrent
                      ? "border-sky-500 bg-sky-900/50 text-sky-200"
                      : answered
                        ? "border-emerald-700/60 bg-emerald-950/30 text-emerald-300"
                        : "border-slate-700 bg-slate-800/60 text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-800">
            <p className="text-xs text-slate-500">
              {totalItems - unansweredCount}/{totalItems} answered
            </p>
          </div>
        </aside>

        {/* ── Question area ──────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto p-6 md:p-10">
          {items.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm" data-testid="no-items">
              No items in this exam.
            </div>
          ) : currentItem ? (
            <div className="max-w-3xl mx-auto">
              {/* Question header */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs text-slate-500 uppercase tracking-wide font-semibold">
                  Question {currentIdx + 1} of {totalItems}
                </span>
                <ItemSaveState
                  itemVersionId={currentItem.item_version_id}
                  saved={savedItems.has(currentItem.item_version_id)}
                  saving={savingItems.has(currentItem.item_version_id)}
                  failed={failedItems.has(currentItem.item_version_id)}
                />
              </div>

              {/* Item type badge */}
              <div className="mb-4">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px]
                                 font-semibold uppercase tracking-wide border
                                 border-slate-700 bg-slate-800/60 text-slate-400">
                  {currentItem.item_type || "Question"}
                </span>
              </div>

              {/* Stem — the actual question text */}
              <div className="mb-8 prose prose-invert prose-sm max-w-none"
                   data-testid={`question-stem-${currentIdx}`}>
                <p className="text-base text-slate-100 leading-relaxed whitespace-pre-wrap">
                  {currentItem.stem}
                </p>
              </div>

              {/* Response area — renders based on item_type */}
              <ResponseWidget
                item={currentItem}
                value={responses[currentItem.item_version_id] || null}
                onChange={(data) => handleResponseChange(currentItem, data)}
                disabled={phase !== "workspace" || timedOut}
              />

              {/* Navigation */}
              <div className="flex justify-between mt-10">
                <button
                  type="button"
                  disabled={currentIdx === 0}
                  onClick={() => setCurrentIdx(i => i - 1)}
                  data-testid="prev-question-btn"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm
                             border border-slate-700 text-slate-300 hover:text-slate-100
                             disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                {currentIdx < totalItems - 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentIdx(i => i + 1)}
                    data-testid="next-question-btn"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm
                               border border-slate-700 text-slate-300 hover:text-slate-100
                               transition-colors"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowConfirmSubmit(true)}
                    data-testid="review-submit-btn"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold
                               bg-emerald-700 hover:bg-emerald-600 text-white transition-colors"
                  >
                    Review &amp; Submit <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </main>
      </div>

      {/* ── Submit confirmation modal ──────────────────────────── */}
      {showConfirmSubmit && (
        <SubmitConfirmModal
          unanswered={unansweredCount}
          total={totalItems}
          submitting={submitting}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirmSubmit(false)}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

function ClipboardList({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2
           M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-800">
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <Icon className="w-4 h-4 text-slate-500" />
        {label}
      </div>
      <span className="text-sm font-medium text-slate-200">{value}</span>
    </div>
  );
}

function ReceiptRow({ label, value, warning }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-xs font-medium text-right ${warning ? "text-amber-300" : "text-slate-200"}`}>
        {value}
      </span>
    </div>
  );
}

function AutosaveIndicator({ saving, failed }) {
  if (failed) return (
    <span className="text-xs text-red-400 flex items-center gap-1"
          data-testid="autosave-failed">
      <AlertTriangle className="w-3 h-3" /> Save failed — retrying…
    </span>
  );
  if (saving) return (
    <span className="text-xs text-amber-400 flex items-center gap-1"
          data-testid="autosave-saving">
      <RotateCcw className="w-3 h-3 animate-spin" /> Saving…
    </span>
  );
  return (
    <span className="text-xs text-slate-500 flex items-center gap-1"
          data-testid="autosave-saved">
      <CheckCircle className="w-3 h-3 text-emerald-500" /> Saved
    </span>
  );
}

function ItemSaveState({ itemVersionId, saved, saving, failed }) {
  if (failed) return <span className="text-[10px] text-red-400 flex items-center gap-1">
    <AlertTriangle className="w-3 h-3" /> Save failed
  </span>;
  if (saving) return <span className="text-[10px] text-amber-400 flex items-center gap-1">
    <RotateCcw className="w-3 h-3 animate-spin" /> Saving…
  </span>;
  if (saved) return <span className="text-[10px] text-emerald-400 flex items-center gap-1">
    <CheckCircle className="w-3 h-3" /> Saved
  </span>;
  return <span className="text-[10px] text-slate-600">Not saved yet</span>;
}

/**
 * ResponseWidget — renders the appropriate response UI for each item_type.
 * NEVER shows correct_response, difficulty, or any scoring metadata.
 * All content comes from the frozen manifest snapshot.
 */
function ResponseWidget({ item, value, onChange, disabled }) {
  const { item_type, options } = item;

  // MCQ — radio buttons (options from manifest, correct_response NEVER shown)
  if (item_type === "MCQ" || item_type === "MULTIPLE_CHOICE") {
    const opts = Array.isArray(options) ? options : [];
    return (
      <fieldset className="space-y-3" data-testid={`mcq-widget-${item.idx}`}>
        <legend className="sr-only">Select your answer</legend>
        {opts.length === 0 && (
          <p className="text-xs text-slate-500 italic">No options available.</p>
        )}
        {opts.map((opt, i) => {
          const optValue = opt.value ?? opt.label ?? opt.text ?? String(i);
          const optText  = opt.text ?? opt.label ?? opt.value ?? `Option ${i + 1}`;
          const isSelected = value?.selected === optValue;
          return (
            <label
              key={optValue}
              data-testid={`mcq-option-${item.idx}-${i}`}
              className={`flex items-start gap-3 rounded-xl border px-5 py-4 cursor-pointer
                         transition-all ${
                disabled ? "cursor-default opacity-70" : "cursor-pointer"
              } ${
                isSelected
                  ? "border-sky-600 bg-sky-950/40"
                  : "border-slate-700 bg-slate-800/40 hover:border-slate-600"
              }`}
            >
              <input
                type="radio"
                name={`item-${item.item_version_id}`}
                value={optValue}
                checked={isSelected}
                disabled={disabled}
                onChange={() => !disabled && onChange({ selected: optValue })}
                className="mt-0.5 accent-sky-500 flex-shrink-0"
              />
              <span className="text-sm text-slate-200 leading-relaxed">{optText}</span>
            </label>
          );
        })}
      </fieldset>
    );
  }

  // Multiple-select
  if (item_type === "MULTIPLE_SELECT" || item_type === "MRS") {
    const opts = Array.isArray(options) ? options : [];
    const selected = Array.isArray(value?.selected) ? value.selected : [];
    const toggle = (optValue) => {
      const next = selected.includes(optValue)
        ? selected.filter(v => v !== optValue)
        : [...selected, optValue];
      onChange({ selected: next });
    };
    return (
      <fieldset className="space-y-3" data-testid={`ms-widget-${item.idx}`}>
        <legend className="sr-only">Select all that apply</legend>
        <p className="text-xs text-slate-500 mb-2">Select all that apply.</p>
        {opts.map((opt, i) => {
          const optValue = opt.value ?? opt.label ?? String(i);
          const optText  = opt.text ?? opt.label ?? optValue;
          const isSelected = selected.includes(optValue);
          return (
            <label
              key={optValue}
              data-testid={`ms-option-${item.idx}-${i}`}
              className={`flex items-start gap-3 rounded-xl border px-5 py-4 transition-all ${
                disabled ? "cursor-default opacity-70" : "cursor-pointer"
              } ${
                isSelected
                  ? "border-sky-600 bg-sky-950/40"
                  : "border-slate-700 bg-slate-800/40 hover:border-slate-600"
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                onChange={() => !disabled && toggle(optValue)}
                className="mt-0.5 accent-sky-500 flex-shrink-0"
              />
              <span className="text-sm text-slate-200 leading-relaxed">{optText}</span>
            </label>
          );
        })}
      </fieldset>
    );
  }

  // Short answer / essay / default
  const isEssay = ["ESSAY", "EXTENDED_RESPONSE", "LONG_ANSWER"].includes(item_type);
  const rows = isEssay ? 10 : 3;
  return (
    <div data-testid={`text-widget-${item.idx}`}>
      <textarea
        value={value?.text || ""}
        onChange={e => !disabled && onChange({ text: e.target.value })}
        disabled={disabled}
        rows={rows}
        placeholder={isEssay ? "Write your response here…" : "Enter your answer…"}
        data-testid={`text-response-${item.idx}`}
        className="w-full bg-slate-800/60 border border-slate-700 rounded-xl px-5 py-4
                   text-sm text-slate-100 placeholder-slate-600 resize-y
                   focus:outline-none focus:border-sky-600 transition-colors
                   disabled:opacity-70 disabled:cursor-default"
      />
      {value?.text && (
        <p className="text-xs text-slate-600 mt-1 text-right">
          {value.text.length} char{value.text.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

function SubmitConfirmModal({ unanswered, total, submitting, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         data-testid="submit-confirm-modal">
      <div className="max-w-sm w-full rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden">
        <div className="px-6 py-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-900/40 flex items-center justify-center">
              <Send className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-100">Submit Exam?</h2>
              <p className="text-xs text-slate-400">This action cannot be undone.</p>
            </div>
          </div>

          {unanswered > 0 && (
            <div className="rounded-xl bg-amber-950/20 border border-amber-800/40 p-4 mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-xs text-amber-200">
                  <strong>{unanswered}</strong> of {total} question{total !== 1 ? "s" : ""}
                  {" "}unanswered. Unanswered questions will receive no marks.
                </p>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400 mb-6">
            {unanswered === 0
              ? `All ${total} question${total !== 1 ? "s" : ""} answered. Ready to submit.`
              : "You can go back to answer remaining questions before submitting."
            }
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              data-testid="confirm-submit-btn"
              className="flex-1 py-3 rounded-xl text-sm font-semibold bg-emerald-700
                         hover:bg-emerald-600 text-white transition-colors disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit Now"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              data-testid="cancel-submit-btn"
              className="px-4 py-3 rounded-xl text-sm text-slate-400 hover:text-slate-200
                         border border-slate-700 transition-colors disabled:opacity-50"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
